import React from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useModuleContext } from '@/contexts/ModuleContext';
import { LoadingScreen } from '@/components/ui/LoadingSpinner';
import { hiddenPath } from '@/components/layout/navConfig';

// The names users see (backend app/modules/registry.py display_name)
const MODULE_NAMES: Record<string, string> = {
  sbl: 'Stock',                     // one client-facing name (the menu's); "Stock & Billing Links" is internal
  bankrec: 'Bank Reconciliation',
  close: 'Month-end close',         // the menu's name
  cashapp: 'Cash Application',
  intercompany: 'Intercompany',
};

/** The page a module route shows when this user can't open the module: why, and the way back. */
export const ModuleUnavailable: React.FC<{ module: string }> = ({ module }) => {
  const { isOnInstance, isError } = useModuleContext();
  const name = MODULE_NAMES[module] ?? module;
  const reason = isError
    ? "We couldn't check which modules you can use. Reload the page to try again."
    : isOnInstance(module) === false
      ? `${name} isn't part of this system.`
      : `You don't have access to ${name}. An administrator can grant it under Users.`;
  return (
    <div className="max-w-lg mx-auto mt-16 text-center" role="alert" data-testid="module-unavailable">
      <Lock className="mx-auto h-10 w-10 text-gray-400" aria-hidden />
      <h1 className="mt-4 text-lg font-semibold text-gray-900">{name} is not available</h1>
      <p className="mt-2 text-sm text-gray-600">{reason}</p>
      <Link to="/dashboard" className="mt-6 inline-block text-sm font-medium text-blue-600 hover:text-blue-700">
        Back to the dashboard
      </Link>
    </div>
  );
};

interface ModuleRouteProps {
  module: string;
  children?: React.ReactNode;          // none: used as a layout route, renders the matched child route
  fallback?: React.ReactNode;          // default: the 'not available' page
}

/** Route-level guard (MODULE_SYSTEM_SPEC §3.4): the module's pages open only when it is on this instance and
 *  granted to the user (/api/modules lists exactly those); otherwise a page saying so, not a page of 403s. */
export const ModuleRoute: React.FC<ModuleRouteProps> = ({ module, children, fallback }) => {
  const { hasModule, isLoading } = useModuleContext();
  if (isLoading) return <LoadingScreen message="Loading page..." />;
  if (!hasModule(module)) return <>{fallback ?? <ModuleUnavailable module={module} />}</>;
  return <>{children ?? <Outlet />}</>;
};

/** Layout route for a page this deployment may hide (config.js HIDE_NAV, as the menus decide): a direct link to a
 *  hidden page goes to ``to`` instead (Davin, 15 Oct: the demo hides Stock's Parallel run, Norbain's SBL parallel
 *  run). HIDE_NAV stays menu-only for every other page. */
export const HideableRoute: React.FC<{ to: string; children?: React.ReactNode }> = ({ to, children }) => {
  const { pathname } = useLocation();
  if (hiddenPath(pathname)) return <Navigate to={to} replace />;
  return <>{children ?? <Outlet />}</>;
};

interface ModuleGuardProps {
  module: string;
  children: React.ReactNode;
}

/** Inline guard — renders children only if the module is enabled. Use for nav items and UI sections. */
export const ModuleGuard: React.FC<ModuleGuardProps> = ({ module, children }) => {
  const { hasModule } = useModuleContext();
  return hasModule(module) ? <>{children}</> : null;
};
